import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { conversationApi } from '../services/conversationApi';
import type { Conversation } from '../types/chat';
import { authService } from '../services/authService';
import { formatDateRelative } from '../utils/dateUtils';
import { ROUTES } from '../constants/routes';
import { ConfirmDialog } from './ConfirmDialog';

interface ConversationSidebarProps {
  currentConversationId?: string;
  onNewConversation: () => void;
  refreshTrigger?: number; // Optional trigger to force refresh
}

export const ConversationSidebar: React.FC<ConversationSidebarProps> = ({
  currentConversationId,
  onNewConversation,
  refreshTrigger,
}) => {
  const navigate = useNavigate();
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [lastRefreshedConversationId, setLastRefreshedConversationId] = useState<string | undefined>();
  const [conversationToDelete, setConversationToDelete] = useState<string | null>(null);

  useEffect(() => {
    loadConversations();
  }, []);

  // Refresh when explicitly triggered (e.g., after message sent)
  useEffect(() => {
    if (refreshTrigger) {
      loadConversations();
    }
  }, [refreshTrigger]);

  // Refresh conversation list when a new conversation is created
  useEffect(() => {
    if (currentConversationId && currentConversationId !== lastRefreshedConversationId) {
      // New conversation detected - refresh the list after a delay
      // to ensure the backend has processed and saved the first message
      const timeoutId = setTimeout(() => {
        loadConversations();
      }, 2000);

      setLastRefreshedConversationId(currentConversationId);

      return () => clearTimeout(timeoutId);
    }
  }, [currentConversationId, lastRefreshedConversationId]);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const loadConversations = async () => {
    if (!authService.isAuthenticated()) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const data = await conversationApi.getRecentConversations(20);
      setConversations(data || []);
    } catch (err) {
      console.error('Failed to load conversations:', err);
      // Set empty array if loading fails - user can still start new conversation
      setConversations([]);
      // Only show error for non-auth issues
      if (err instanceof Error && !err.message.includes('Authentication')) {
        setError('Unable to load conversation history');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteConversation = (conversationId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setConversationToDelete(conversationId);
  };

  const confirmDelete = async () => {
    if (!conversationToDelete) return;

    try {
      await conversationApi.deleteConversation(conversationToDelete);
      setConversations(prev => prev.filter(c => c.conversationId !== conversationToDelete));
      if (currentConversationId === conversationToDelete) {
        navigate('/');
        onNewConversation();
      }
    } catch (err) {
      console.error('Failed to delete conversation:', err);
    } finally {
      setConversationToDelete(null);
    }
  };

  const cancelDelete = () => {
    setConversationToDelete(null);
  };


  if (!authService.isAuthenticated()) {
    return null;
  }

  return (
    <>
      {/* Mobile toggle button */}
      {isMobile && (
        <button
          className="mobile-toggle-btn"
          onClick={() => setIsOpen(!isOpen)}
          aria-label="Toggle conversation history"
        >
          <svg className="mobile-toggle-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      )}

      {/* Sidebar */}
      <div className="conversation-sidebar">
        {/* Desktop sidebar content */}
        {/* Header */}
        <div className="conversation-sidebar-header">
          <button
            onClick={onNewConversation}
            className="conversation-sidebar-new-chat-btn"
          >
            <svg className="conversation-sidebar-new-chat-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            New Chat
          </button>
        </div>

        {/* Conversations list */}
        <div className="conversation-sidebar-list">
          {isLoading ? (
            <div className="conversation-sidebar-loading">Loading...</div>
          ) : error ? (
            <div className="conversation-sidebar-error">{error}</div>
          ) : conversations.length === 0 ? (
            <div className="conversation-sidebar-empty">No conversations yet</div>
          ) : (
            <div className="conversation-sidebar-conversations">
              {conversations.map((conversation) => (
                <Link
                  key={conversation.conversationId}
                  to={ROUTES.getConversationRoute(conversation.conversationId)}
                  className={`conversation-item ${
                    currentConversationId === conversation.conversationId ? 'active' : ''
                  }`}
                  onClick={() => setIsOpen(false)}
                >
                  <div className="conversation-item-content">
                    <div className="conversation-item-title">
                      {conversation.title}
                    </div>
                    <div className="conversation-item-meta">
                      {formatDateRelative(conversation.updatedAt)} · {conversation.messageCount} messages
                    </div>
                  </div>
                  <button
                    onClick={(e) => handleDeleteConversation(conversation.conversationId, e)}
                    className="conversation-item-delete"
                    aria-label="Delete conversation"
                  >
                    <svg className="conversation-item-delete-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                  </button>
                </Link>
              ))}
            </div>
          )}
        </div>

        {/* Footer with refresh button */}
        <div className="conversation-sidebar-footer">
          <button
            onClick={loadConversations}
            className="conversation-sidebar-refresh-btn"
          >
            <svg className="conversation-sidebar-refresh-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            Refresh
          </button>
        </div>
      </div>

      {/* Mobile Sidebar Overlay - Only show on mobile */}
      {isOpen && isMobile && (
        <>
          <div
            className="mobile-sidebar-overlay"
            onClick={() => setIsOpen(false)}
          />
          <div className="mobile-sidebar">
            {/* Mobile Header */}
            <div className="conversation-sidebar-header">
              <button
                onClick={() => {
                  onNewConversation();
                  setIsOpen(false);
                }}
                className="conversation-sidebar-new-chat-btn"
              >
                <svg className="conversation-sidebar-new-chat-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                New Chat
              </button>
            </div>

            {/* Mobile Conversations list */}
            <div className="conversation-sidebar-list">
              {isLoading ? (
                <div className="conversation-sidebar-loading">Loading...</div>
              ) : error ? (
                <div className="conversation-sidebar-error">{error}</div>
              ) : conversations.length === 0 ? (
                <div className="conversation-sidebar-empty">No conversations yet</div>
              ) : (
                <div className="conversation-sidebar-conversations">
                  {conversations.map((conversation) => (
                    <Link
                      key={conversation.conversationId}
                      to={ROUTES.getConversationRoute(conversation.conversationId)}
                      className={`conversation-item ${
                        currentConversationId === conversation.conversationId ? 'active' : ''
                      }`}
                      onClick={() => setIsOpen(false)}
                    >
                      <div className="conversation-item-content">
                        <div className="conversation-item-title">
                          {conversation.title}
                        </div>
                        <div className="conversation-item-meta">
                          {formatDateRelative(conversation.updatedAt)} · {conversation.messageCount} messages
                        </div>
                      </div>
                      <button
                        onClick={(e) => handleDeleteConversation(conversation.conversationId, e)}
                        className="conversation-item-delete"
                        aria-label="Delete conversation"
                      >
                        <svg className="conversation-item-delete-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Mobile Footer with refresh button */}
            <div className="conversation-sidebar-footer">
              <button
                onClick={loadConversations}
                className="conversation-sidebar-refresh-btn"
              >
                <svg className="conversation-sidebar-refresh-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Refresh
              </button>
            </div>
          </div>
        </>
      )}

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        isOpen={conversationToDelete !== null}
        title="Delete Conversation"
        message="Are you sure you want to delete this conversation? This action cannot be undone."
        confirmLabel="Delete"
        cancelLabel="Cancel"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
      />
    </>
  );
};